'use client'

import { isEmpty, isNil } from 'lodash-es'
import { useTranslations } from 'next-intl'
import { RefObject, useEffect, useState } from 'react'

import { ZoomIn } from '@/entities/map'
import { NoSearchResult } from '@/entities/room'
import { cn } from '@/shared/lib'
import { Spinner } from '@/shared/ui'

import { useMapToast } from '../../lib/toast'
import { useRooms } from '../../model/rooms'
import { MobileRoomItem } from '../room-item'

const ZoomInMessage = () => {
  const t = useTranslations('map')

  return (
    <div
      className={cn(
        'mt-7 flex w-full shrink-0 flex-col items-center justify-center gap-6 md:col-span-2 landscape:mt-0',
        'transition-all duration-300 group-data-[half-collapsed=false]:mt-25 group-data-[half-collapsed=false]:landscape:mt-0',
      )}
    >
      <ZoomIn />
      <h3 className="typo-headline3 text-center font-bold text-black">
        {t('list.zoom_message')}
      </h3>
    </div>
  )
}

const NoResultMessage = () => {
  const t = useTranslations('map')

  return (
    <div
      className={cn(
        'flex w-full shrink-0 flex-col items-center justify-center md:col-span-2',
        'transition-all duration-300 group-data-[half-collapsed=false]:mt-25 group-data-[half-collapsed=false]:landscape:mt-0',
      )}
    >
      <NoSearchResult className="mb-6" />
      <h3 className="typo-headline3 mb-4 text-center font-bold text-black">
        {t('list.no_result.title')}
      </h3>
      <p className="typo-label1 text-center whitespace-break-spaces text-gray-900">
        {t('list.no_result.description')}
      </p>
    </div>
  )
}

type InnerRoomListProps = {
  isMounted: boolean
  scrollToTop: () => void
}

const InnerRoomList = ({ isMounted, scrollToTop }: InnerRoomListProps) => {
  const { rooms, ref, loading, isFirstPage, isFetching } = useRooms({
    isMounted,
  })

  useMapToast({ isMounted, rooms, isFetching })

  useEffect(() => {
    if (isFirstPage) {
      scrollToTop()
    }
  }, [rooms])

  if (!isMounted || loading)
    return <Spinner className="mt-[140px] items-start" />
  if (isNil(rooms)) return <ZoomInMessage />
  if (isEmpty(rooms)) return <NoResultMessage />

  const GRID_COLUMNS = 2

  return rooms.map((room, index) => {
    const isLastItem = index === rooms.length - 1
    const isFirstRow = index < GRID_COLUMNS
    const isLastRow =
      index >= rooms.length - (rooms.length % GRID_COLUMNS || GRID_COLUMNS)

    return (
      <li
        key={room.rid}
        className={cn(
          'group border-b border-gray-100 last:border-none',
          isLastRow && 'md:border-none',
        )}
        ref={isLastItem ? ref : null}
      >
        <MobileRoomItem
          room={room}
          className={cn(
            'group-first:pt-0 group-last:pb-0',
            isFirstRow && 'md:pt-0',
            isLastRow && 'md:pb-0',
          )}
        />
      </li>
    )
  })
}

type MobileRoomListProps = {
  ref: RefObject<HTMLUListElement | null>
  onScroll: () => void
}

export const MobileRoomList = ({
  ref: refProp,
  onScroll,
}: MobileRoomListProps) => {
  const [isMounted, setIsMounted] = useState(false)

  const scrollToTop = () => {
    refProp.current?.scroll({ top: 0, behavior: 'smooth' })
  }

  useEffect(() => {
    setIsMounted(true)
  }, [])

  return (
    <ul
      ref={refProp}
      onScroll={onScroll}
      className={cn(
        'mb-5 flex-1 overflow-y-auto overscroll-contain px-4 pt-1 md:grid md:grid-cols-2 md:content-start md:gap-x-10',
      )}
    >
      <InnerRoomList isMounted={isMounted} scrollToTop={scrollToTop} />
    </ul>
  )
}
